/**
 * 高德 JSAPI 2.0 的按需单例加载器。
 *
 * - **懒加载**：只有真正要渲染地图的页面才会触发，登录页、无人机管理页等完全不受影响；
 * - **单例**：总览页与单机页共用同一个 Promise，不会重复插入 script 标签；
 * - **失败可重试**：加载失败后清掉缓存的 Promise，网络恢复后下一次仍能重试。
 */

const SCRIPT_ID = 'havorix-amap-jsapi';
/** 两个插件都只有几十 KB，随主脚本一次取回，省去二次回调 */
const PLUGINS = ['AMap.ToolBar', 'AMap.Scale'];

export const amapKey = (import.meta.env.VITE_AMAP_KEY ?? '').trim();
const securityCode = (import.meta.env.VITE_AMAP_SECURITY_CODE ?? '').trim();

let pending: Promise<typeof AMap> | null = null;

/** 没配置 Key 时调用方应直接展示提示，而不是等一个必然失败的请求 */
export function isAmapConfigured(): boolean {
  return amapKey !== '';
}

export function loadAMap(): Promise<typeof AMap> {
  if (window.AMap) return Promise.resolve(window.AMap);
  if (!isAmapConfigured()) return Promise.reject(new Error('未配置 VITE_AMAP_KEY'));
  if (pending) return pending;

  pending = new Promise<typeof AMap>((resolve, reject) => {
    // 安全密钥必须在脚本执行前挂上去，否则会报 INVALID_USER_SCODE。
    if (securityCode !== '') {
      window._AMapSecurityConfig = { securityJsCode: securityCode };
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(amapKey)}&plugin=${PLUGINS.join(
      ',',
    )}`;
    script.addEventListener('load', () => {
      if (window.AMap) resolve(window.AMap);
      else reject(new Error('高德 JSAPI 已加载但未注册 window.AMap'));
    });
    script.addEventListener('error', () => reject(new Error('高德 JSAPI 脚本加载失败')));
    document.head.appendChild(script);
  });

  pending.catch(() => {
    pending = null;
    document.getElementById(SCRIPT_ID)?.remove();
  });

  return pending;
}
